import { useState } from "react";
import { useRouteLoaderData, useNavigate } from "react-router";
import { SidebarProvider } from "@/components/ui/sidebar";
import { WorkspaceSidebar } from "@/components/WorkspaceSidebar";
import { SettingsDialog } from "@/components/SettingsDialog";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { FolderPlus } from "lucide-react";
import {
	WORKSPACE_LOADER_ID,
	type WorkspaceLoaderData,
} from "@/routes/workspaceLoader";
import { useWorkspaceSidebarActions } from "@/hooks/useWorkspaceSidebarActions";

const AllProjects = () => {
	const navigate = useNavigate();
	const { projects, codes } = useRouteLoaderData(
		WORKSPACE_LOADER_ID,
	) as WorkspaceLoaderData;
	const [settingsOpen, setSettingsOpen] = useState(false);
	const { createProject, createCode, updateProjectTitle, updateCodeTitle } =
		useWorkspaceSidebarActions();

	const handleProjectClick = (projectId: string) => {
		navigate(`/workspace/evals/${projectId}`);
	};

	return (
		<SidebarProvider>
			<div className="min-h-screen flex w-full">
				<WorkspaceSidebar
					projects={projects}
					codes={codes}
					onNewProject={createProject}
					onNewCode={createCode}
					onUpdateProjectTitle={updateProjectTitle}
					onUpdateCodeTitle={updateCodeTitle}
					onOpenSettings={() => setSettingsOpen(true)}
				/>
				<main className="flex-1 overflow-auto">
					<div className="container mx-auto py-8 px-4">
						<div className="mb-6 flex items-start justify-between">
							<div>
								<h1
									className="text-4xl font-bold text-foreground"
									data-testid="evals-heading"
								>
									All Evals
								</h1>
								<p className="text-muted-foreground mt-2">
									Browse and select from all your evals
								</p>
							</div>
							<Button onClick={() => createProject()} size="lg">
								<FolderPlus className="mr-2 h-4 w-4" />
								Create New
							</Button>
						</div>

						{projects.length === 0 ? (
							<Card>
								<CardContent className="py-12 text-center">
									<FolderPlus className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
									<p className="text-muted-foreground">No evals found</p>
								</CardContent>
							</Card>
						) : (
							<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
								{projects.map((project) => (
									<Card
										key={project.id}
										className="cursor-pointer hover:border-primary transition-colors"
										onClick={() => handleProjectClick(project.id)}
									>
										<CardHeader>
											<div className="flex items-center gap-2">
												<FolderPlus className="h-5 w-5 text-primary" />
												<CardTitle className="text-lg">
													{project.title}
												</CardTitle>
											</div>
											<CardDescription className="text-xs mt-2">
												Created{" "}
												{new Date(project.created_at).toLocaleDateString()}
											</CardDescription>
										</CardHeader>
										<CardContent>
											<div className="space-y-2">
												<div>
													<span className="text-xs font-semibold text-muted-foreground">
														Last Updated:
													</span>
													<p className="text-sm">
														{new Date(project.updated_at).toLocaleDateString()}
													</p>
												</div>
											</div>
										</CardContent>
									</Card>
								))}
							</div>
						)}
					</div>
				</main>
				<SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
			</div>
		</SidebarProvider>
	);
};

export default AllProjects;
